import './AccordionsPage.scss';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import AppAccordion from '../AppAccordion/AppAccordion';
import CodeSnippet from '../CodeSnippet/CodeSnippet';
import parse, {
  domToReact,
  HTMLReactParserOptions,
  Element,
  DOMNode
} from 'html-react-parser';
import ReactDOMServer from 'react-dom/server';

const AccordionsPage = ({ accordions }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const key = JSON.stringify(accordions);

  const convertTableToMobileView = (htmlString: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    const headers = Array.from(doc.querySelectorAll('thead th')).map((th) =>
      th.textContent?.trim()
    );
    const rows = doc.querySelectorAll('tbody tr');

    let mobileViewHtml = Array.from(rows)
      .map((row) => {
        const cells = row.querySelectorAll('td');
        let rowHtml = '<div class="mobile-row">';

        cells.forEach((cell, index) => {
          rowHtml += `
          <div class="mobile-subrow">
            <p class="mobile-subrow-header">${headers[index]}</p>
            <p>${cell.textContent?.trim()}</p>
          </div>
        `;
        });

        rowHtml += '</div>';
        return rowHtml;
      })
      .join('');

    mobileViewHtml = `<div class="mobile-table-view">${mobileViewHtml}</div>`;

    const tableElement = doc.querySelector('table');
    if (tableElement) {
      tableElement.outerHTML = mobileViewHtml;
    }

    return doc.body.innerHTML;
  };

    // Add this new function for accessibility only
  // const addAccessibilityContext = (htmlString: string, accordionSummary: string) => {
  //   const parser = new DOMParser();
  //   const doc = parser.parseFromString(htmlString, 'text/html');
    
  //   // Find all headings in the content
  //   const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    
  //   headings.forEach((heading, index) => {
  //     const originalText = heading.textContent?.trim() || '';
      
  //     // Extract environment type from accordion summary for context
  //     const environmentMatch = accordionSummary.match(/(UAT|BAT|Production)/i);
  //     const environmentType = environmentMatch ? environmentMatch[1] : '';
      
  //     // Add aria-label for screen readers without changing visible text
  //     let accessibleLabel = originalText;
      
  //     if (originalText.toLowerCase().includes('service urls') || 
  //         originalText.toLowerCase().includes('resources endpoints')) {
  //       accessibleLabel = environmentType ? `${environmentType} Service URLs Resources Endpoints` : `${accordionSummary} Service URLs`;
  //     } else if (originalText.toLowerCase().includes('api key')) {
  //       accessibleLabel = environmentType ? `${environmentType} API Key` : `${accordionSummary} API Key`;
  //     } else if (originalText.toLowerCase().includes('trace id')) {
  //       accessibleLabel = environmentType ? `${environmentType} Trace ID` : `${accordionSummary} Trace ID`;
  //     } else if (originalText.toLowerCase().includes('message structure example')) {
  //       accessibleLabel = `${accordionSummary} Message Structure Example`;
  //     } else if (originalText.toLowerCase().includes('ndc message structure example')) {
  //       accessibleLabel = `${accordionSummary} NDC Message Structure Example`;
  //     }
      
  //     // Add aria-label for screen readers (visible text stays the same)
  //     if (accessibleLabel !== originalText) {
  //       heading.setAttribute('aria-label', accessibleLabel);
  //     }
      
  //     // Add unique ID for better navigation
  //     const safeId = `${accordionSummary.toLowerCase().replace(/[^a-z0-9]/g, '-')}-heading-${index}`;
  //     heading.id = safeId;
  //   });
    
  //   return doc.body.innerHTML;
  // };

  const getAccordionDetails = (accordion) => {
    if (accordion.sub_accordions && accordion.sub_accordions.length > 0) {
      return accordion.sub_accordions.map((subAccordion) => (
        <Box mb={1.25} sx={{ background: 'white !important' }} className="sub-accordion">
          <AppAccordion
            key={subAccordion.id}
            details={getAccordionDetails(subAccordion)}
            summary={subAccordion.summary}
          />
        </Box>
      ));
    }
    // Add accessibility context without changing visible content
    // let processedDetails = addAccessibilityContext(accordion.details, accordion.summary);
    if (isMobile) {
      return convertTableToMobileView(transformPreTag(accordion.details,accordion.summary));
    }
    return transformPreTag(accordion.details,accordion.summary);
  };

  const promoteServiceUrlsHeading = (htmlString: string) => {
    if (!/service urls/i.test(htmlString)) {
      return htmlString;
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    doc.querySelectorAll('p, h1, h2, h4, h5, h6').forEach((el) => {
      const text = el.textContent?.trim() || '';
      if (/^service urls/i.test(text)) {
        const h3 = doc.createElement('h3');
        h3.textContent = text;
        h3.style.marginTop = '18px';
        h3.style.marginBottom = '18px';
        el.replaceWith(h3);
      }
    });
    return doc.body.innerHTML;
  };

  const transformPreTag = (htmlString: string,summary: string) => {
    const transformedHtmlString = promoteServiceUrlsHeading(htmlString)
      .replace(
        /<pre>([\s\S]*?)<\/pre>/g,
        (_, p1) => `<codesnippet>${p1}</codesnippet>`
      );

    const options: HTMLReactParserOptions = {
      replace: (node) => {
        if (
          node instanceof Element &&
          node.tagName.toLowerCase() === 'codesnippet'
        ) {
          const content = domToReact(node.children as DOMNode[]);
          return <CodeSnippet ariaLabel={"Copy "+summary}>{content}</CodeSnippet>;
        }
      }
    };

    const reactElement = parse(transformedHtmlString, options);

    return ReactDOMServer.renderToStaticMarkup(
      reactElement as React.ReactElement
    );
  };

  return (
    <div className="accordions-page" key={key}>
      {Array.isArray(accordions) ? (
        accordions.map((accordion) => (
          <AppAccordion
            key={accordion.id}
            details={getAccordionDetails(accordion)}
            summary={accordion.summary}
            headingLevel='h2'
          />
        ))
      )
        : (
          <div dangerouslySetInnerHTML={{ __html: accordions }}></div>
        )
      }
    </div>
  )
};

export default AccordionsPage;
